import { useState, useEffect } from 'react';
import { useGalleryApprovals } from './useGalleryApprovals';
import { useImageApprovals } from './useImageApprovals';
import { useToast } from '@/hooks/use-toast';
import type { ApprovalItem, GalleryAllImage } from '@/types/gallery';

export interface ApprovalQueueHook {
  pendingGalleries: ApprovalItem[];
  pendingImages: GalleryAllImage[];
  isLoading: boolean;
  galleriesLoading: boolean;
  imagesLoading: boolean;
  error: string | null;
  refreshQueue: () => Promise<void>;
  approveGallery: (galleryId: number) => Promise<boolean>;
  rejectGallery: (galleryId: number) => Promise<boolean>;
  flagGallery: (galleryId: number) => Promise<boolean>;
  approveImage: (imageId: number, imageType?: 'gallery_photo' | 'job_image') => Promise<boolean>;
  rejectImage: (imageId: number, imageType?: 'gallery_photo' | 'job_image') => Promise<boolean>;
  flagImage: (imageId: number, imageType?: 'gallery_photo' | 'job_image') => Promise<boolean>;
}

export const useApprovalQueue = (): ApprovalQueueHook => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  
  const { 
    pendingGalleries, 
    isLoading: galleriesLoading,
    error: galleriesError,
    fetchPendingGalleries, 
    approveGallery, 
    rejectGallery, 
    flagGallery 
  } = useGalleryApprovals();

  const { 
    pendingImages, 
    isLoading: imagesLoading,
    error: imagesError,
    fetchPendingImages, 
    approveImage, 
    rejectImage, 
    flagImage 
  } = useImageApprovals();

  useEffect(() => {
    // Initial data fetch
    refreshQueue();
  }, []);

  useEffect(() => {
    // Update combined loading state
    setIsLoading(galleriesLoading || imagesLoading);
  }, [galleriesLoading, imagesLoading]);

  useEffect(() => {
    // Update combined error state
    const combinedError = galleriesError || imagesError;
    setError(combinedError);
  }, [galleriesError, imagesError]);

  const refreshQueue = async (): Promise<void> => {
    try {
      console.log('Refreshing approval queue...');
      
      await Promise.all([
        fetchPendingGalleries(),
        fetchPendingImages()
      ]);
      
      console.log('Approval queue refreshed successfully');
    } catch (error) {
      console.error('Error refreshing approval queue:', error);
      toast({
        title: 'Error',
        description: 'Failed to refresh approval queue',
        variant: 'destructive'
      });
    }
  };

  // Wrap approval functions to include refresh
  const handleApproveGallery = async (galleryId: number): Promise<boolean> => {
    const success = await approveGallery(galleryId);
    if (success) {
      // Optional: refresh other data or update stats
    }
    return success;
  };

  const handleRejectGallery = async (galleryId: number): Promise<boolean> => {
    const success = await rejectGallery(galleryId);
    if (success) {
      // Optional: refresh other data or update stats
    }
    return success;
  };

  const handleFlagGallery = async (galleryId: number): Promise<boolean> => {
    const success = await flagGallery(galleryId);
    if (success) {
      // Optional: refresh other data or update stats
    }
    return success;
  };

  const handleApproveImage = async (imageId: number, imageType?: 'gallery_photo' | 'job_image'): Promise<boolean> => {
    const success = await approveImage(imageId, imageType);
    if (success) {
      // Optional: refresh other data or update stats
    }
    return success;
  };

  const handleRejectImage = async (imageId: number, imageType?: 'gallery_photo' | 'job_image'): Promise<boolean> => {
    const success = await rejectImage(imageId, imageType);
    if (success) {
      // Optional: refresh other data or update stats
    }
    return success;
  };

  const handleFlagImage = async (imageId: number, imageType?: 'gallery_photo' | 'job_image'): Promise<boolean> => {
    const success = await flagImage(imageId, imageType);
    if (success) {
      // Optional: refresh other data or update stats
    }
    return success;
  };

  return {
    pendingGalleries,
    pendingImages,
    isLoading,
    galleriesLoading,
    imagesLoading,
    error,
    refreshQueue,
    approveGallery: handleApproveGallery,
    rejectGallery: handleRejectGallery,
    flagGallery: handleFlagGallery,
    approveImage: handleApproveImage,
    rejectImage: handleRejectImage,
    flagImage: handleFlagImage
  };
};